import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthUser, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import AppRouter from './AppRouter';
import activityEvents from '../services/activityEvents';
import logger from '../services/logger';
import { LAST_ACTIVITY_KEY, INACTIVITY_TIMEOUT } from '../constants';

interface AppContentProps {
  initialUser: AuthUser | undefined;
  updateAuthToken: () => Promise<void>;
}

export default function AppContent({ initialUser, updateAuthToken }: AppContentProps): JSX.Element {
  const [user, setUser] = useState<AuthUser | undefined>(initialUser);
  const navigate = useNavigate();
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef(Date.now());

  // Store the last activity timestamp in local storage so that the inactivity is accounted for even when the broswer tab is closed 
  const updateLastActivity = useCallback((): void => {
    const timestamp = Date.now();
    lastActivityRef.current = timestamp;
    localStorage.setItem(LAST_ACTIVITY_KEY, timestamp.toString()); 
  }, []);

  const handleSignOut = useCallback(async (): Promise<void> => {
    try {
      await signOut();
      setUser(undefined);
      await updateAuthToken();
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      navigate('/');
    } catch (error) {
      await logger.log(`Error signing out: ${error}`, { source: { file: __filename } });
    }
  }, [navigate, updateAuthToken]);

  const checkInactivity = useCallback((): void => {
    const currentTime = Date.now();
    const lastActivity = lastActivityRef.current;
    const timeSinceLastActivity = currentTime - lastActivity;

    if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
      handleSignOut();
    } else {
      inactivityTimerRef.current = setTimeout(checkInactivity, 60000); 
    }
  }, [handleSignOut]);

  const resetInactivityTimer = useCallback((): void => {
    updateLastActivity();
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    inactivityTimerRef.current = setTimeout(checkInactivity, 60000); 
  }, [updateLastActivity, checkInactivity]);

  const handleAuthToken = useCallback(async (): Promise<void> => {
    try {
      await updateAuthToken(); 
    } catch (error) {
      await logger.log(`Error updating auth token: ${error}`, { source: { file: __filename } });
      await handleSignOut();
    }
  }, [updateAuthToken, handleSignOut]);

  useEffect(() => {
    const checkIfInactiveOnLoad = (): void => {
      const lastActivityTimestamp = localStorage.getItem(LAST_ACTIVITY_KEY);
      if (lastActivityTimestamp) {
        const timeSinceLastActivity = Date.now() - parseInt(lastActivityTimestamp, 10);
        if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
          handleSignOut();
        }
      }
    };

    if (user) {
      updateLastActivity();
      checkIfInactiveOnLoad(); // Check if the user should be logged out on tab reopen
      handleAuthToken();
      resetInactivityTimer();

      const handleUserActivity = (): void => {
        resetInactivityTimer();
      };

      activityEvents.forEach(event => {
        window.addEventListener(event, handleUserActivity);
      });

      return () => {
        activityEvents.forEach(event => {
          window.removeEventListener(event, handleUserActivity);
        });
        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current);
        }
      };
    }
  }, [user, handleAuthToken, resetInactivityTimer, handleSignOut, updateLastActivity]);

  useEffect(() => {
    const handleAuthError = async (data: any): Promise<void> => {
      if (data.payload.event === 'signOut' || data.payload.event === 'tokenRefresh_failure') {
        await handleSignOut();
      }
    };

    const hubListener = Hub.listen('auth', handleAuthError);

    return () => {
      hubListener();
    };
  }, [handleSignOut]);

  return (
    <AppRouter />
  );
}